import React, { useEffect, useState } from "react";
import { crm } from "@/lib/adminApi";
import { toast } from "sonner";
import { X, FileText, Plus } from "lucide-react";

const STATUSES = ["new", "contacted", "proposal_sent", "won", "lost"];
const STATUS_LABEL = { new: "New", contacted: "Contacted", proposal_sent: "Proposal Sent", won: "Won", lost: "Lost" };

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => crm.list(filter).then(setItems).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div data-testid="admin-inquiries">
      <div className="eyebrow mb-3">Pipeline</div>
      <h1 className="font-display text-5xl text-aria-ink">Inquiries</h1>

      <div className="mt-8 flex gap-5 border-b border-aria-travertine pb-3 text-[0.7rem] uppercase tracking-[0.18em]">
        <button onClick={() => setFilter("")} className={!filter ? "text-aria-ink" : "text-aria-muted"}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`crm-filter-${s}`} className={filter === s ? "text-aria-ink" : "text-aria-muted"}>
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="mt-6 divide-y divide-aria-travertine border-y border-aria-travertine bg-aria-ivory">
        {items.map((i) => (
          <button
            key={i.id}
            onClick={() => setSelected(i.id)}
            data-testid={`crm-row-${i.id}`}
            className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-aria-sand"
          >
            <div>
              <div className="font-display text-xl text-aria-ink">{i.name}</div>
              <div className="text-[0.68rem] uppercase tracking-[0.14em] text-aria-muted">
                {i.event_type || "—"} · {i.location || "—"} · {i.guest_count || "—"} · {i.event_date || "no date"}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {i.proposal_sent && <FileText className="h-4 w-4 text-aria-brass" />}
              <span className="text-[0.62rem] uppercase tracking-[0.16em] text-aria-brass">{STATUS_LABEL[i.status] || i.status}</span>
            </div>
          </button>
        ))}
        {items.length === 0 && <div className="px-5 py-10 text-center text-aria-muted">No inquiries.</div>}
      </div>

      {selected && <Detail id={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function Detail({ id, onClose }) {
  const [inq, setInq] = useState(null);
  const [note, setNote] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [form, setForm] = useState({ status: "new", revenue_estimate: "", follow_up_date: "" });

  const load = () => crm.get(id).then((d) => {
    setInq(d);
    setForm({ status: d.status || "new", revenue_estimate: d.revenue_estimate || "", follow_up_date: d.follow_up_date || "" });
  }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    await crm.update(id, form);
    toast.success("Inquiry updated");
    load();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await crm.addNote(id, note.trim());
    setNote("");
    load();
  };
  const generate = async () => {
    setGenLoading(true);
    try {
      const prop = await crm.generateProposal(id);
      toast.success("Proposal generated");
      const url = await crm.proposalPdfUrl(prop.id);
      window.open(url, "_blank");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenLoading(false);
    }
  };
  const viewPdf = async (pid) => {
    const url = await crm.proposalPdfUrl(pid);
    window.open(url, "_blank");
  };

  const input = "w-full border-0 border-b border-aria-travertine bg-transparent py-2 text-aria-ink focus:border-aria-brass focus:outline-none";

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-aria-ink/40" onClick={onClose} data-testid="crm-detail">
      <div className="h-full w-full max-w-xl overflow-y-auto bg-aria-ivory p-8 md:p-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow mb-2">Inquiry</div>
            <h2 className="font-display text-4xl text-aria-ink">{inq?.name}</h2>
            <a href={`mailto:${inq?.email}`} className="aria-link text-sm text-aria-cocoa">{inq?.email}</a>
          </div>
          <button onClick={onClose} data-testid="crm-close"><X className="h-6 w-6 text-aria-ink" /></button>
        </div>

        {inq && (
          <>
            <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
              {[["Phone", inq.phone], ["Event Type", inq.event_type], ["Date", inq.event_date], ["Venue", inq.venue], ["Location", inq.location], ["Guests", inq.guest_count], ["Planner", inq.planner], ["Budget", inq.budget]].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[0.6rem] uppercase tracking-[0.16em] text-aria-muted">{k}</div>
                  <div className="text-aria-ink">{v || "—"}</div>
                </div>
              ))}
            </div>
            {inq.notes && <p className="mt-6 border-l-2 border-aria-brass pl-4 text-aria-cocoa/80">{inq.notes}</p>}

            <div className="mt-8 border-t border-aria-travertine pt-6">
              <div className="eyebrow mb-4">Manage</div>
              <div className="grid grid-cols-1 gap-5">
                <label className="block">
                  <span className="text-[0.6rem] uppercase tracking-[0.16em] text-aria-muted">Status</span>
                  <select data-testid="crm-status" className={input} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[0.6rem] uppercase tracking-[0.16em] text-aria-muted">Revenue Estimate</span>
                  <input data-testid="crm-revenue" className={input} value={form.revenue_estimate} onChange={(e) => setForm({ ...form, revenue_estimate: e.target.value })} placeholder="$0" />
                </label>
                <label className="block">
                  <span className="text-[0.6rem] uppercase tracking-[0.16em] text-aria-muted">Follow-up Reminder</span>
                  <input data-testid="crm-followup" type="date" className={input} value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} />
                </label>
                <button data-testid="crm-save" onClick={save} className="bg-aria-ink px-6 py-3 text-[0.68rem] uppercase tracking-[0.2em] text-aria-ivory">Save changes</button>
              </div>
            </div>

            <div className="mt-8 border-t border-aria-travertine pt-6">
              <div className="mb-4 flex items-center justify-between">
                <div className="eyebrow">AI Proposal</div>
                <button data-testid="crm-generate-proposal" onClick={generate} disabled={genLoading} className="flex items-center gap-2 bg-aria-brass px-5 py-2.5 text-[0.66rem] uppercase tracking-[0.18em] text-aria-ink disabled:opacity-50">
                  <FileText className="h-3.5 w-3.5" /> {genLoading ? "Generating…" : "Generate PDF"}
                </button>
              </div>
              {(inq.proposals || []).map((p) => (
                <button key={p.id} onClick={() => viewPdf(p.id)} className="mb-2 flex w-full items-center justify-between border border-aria-travertine px-4 py-3 text-left hover:bg-aria-sand">
                  <span className="font-display text-lg text-aria-ink">{p.title}</span>
                  <span className="text-[0.6rem] uppercase tracking-[0.16em] text-aria-brass">View PDF</span>
                </button>
              ))}
            </div>

            <div className="mt-8 border-t border-aria-travertine pt-6">
              <div className="eyebrow mb-4">Notes</div>
              <div className="space-y-3">
                {(inq.crm_notes || []).map((n, idx) => (
                  <div key={idx} className="bg-aria-sand p-3 text-sm text-aria-cocoa">{n.text}
                    <div className="mt-1 text-[0.55rem] uppercase tracking-[0.14em] text-aria-muted">{new Date(n.at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <input data-testid="crm-note-input" value={note} onChange={(e) => setNote(e.target.value)} className={input} placeholder="Add a note…" />
                <button data-testid="crm-note-add" onClick={addNote} className="flex items-center bg-aria-ink px-4 text-aria-ivory"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
